import { App } from '@flipdish/api-client-typescript';
import without from 'lodash/without';
import { getTranslate } from 'react-localize-redux';
import { add as addRPermissions, clear as clearPermissions } from 'react-redux-permissions';
import { createAction } from 'redux-act';

import { appsConstants } from '../constants/apps.constants';
import { generalConstants } from '../constants/general.constants';
import { isFlipdishStaffEmail } from '../helpers/account';
import { notifyError } from '../layouts/Notify/actions';
import { history } from '../routes/history';
import { isPublicRoute } from '../routes/utils';
import { appsService } from '../services/apps.service';
import { setFeatureFlagContext } from '../services/featureFlags/featureFlagBootstrap';
import userPermissionsService from '../services/userPermissions.service';
import { action, actionError } from './utils';

export const appsActions = {
  selectedAppChanged,
  getApps: getAppsLegacy,
  getAppsNew,
  processAppsQueue,
  setPageNumber,
  setSearchQuery,
  getAppsWithQuery,
  setCurrentApp,
  toggleAppMenu,
  setPanaceaVanityUrl,
  setPanaceaVanityUrlSuccess,
  getPanaceaVanityUrlSuccess,
  getPanaceaVanityUrlFailure,
  setPanaceaVanityUrlFailure,
};

const setCurrentAppIdInStorage = (appId: string) => {
  localStorage.setItem('fd-currentAppId', appId);
};

let latestAppSelectionRequest = 0;
let latestSetCurrentApp: { request: number; selection: Promise<App | undefined> } | undefined;

/**
 * Installs `app` as the current app and loads its permissions. Called directly (no request id), the
 * selection owns the brand switch and finishes it once the permissions are installed or it bails;
 * `setCurrentApp` passes its own request id and finishes the switch itself, including on lookup
 * failure. A request superseded by a newer one leaves finishing to that newer request.
 */
function selectedAppChanged(app: App, requestedSelection?: number) {
  const isDirectSelection = requestedSelection === undefined;
  const appSelectionRequest = requestedSelection ?? ++latestAppSelectionRequest;
  return async (dispatch, getState: () => AppState) => {
    try {
      if (appSelectionRequest !== latestAppSelectionRequest) {
        return;
      }

      const appId = getState().currentApp.AppId;
      const appChanged = appId !== app.AppId;
      const permissionsLoaded = getState().permissions.length > 0;

      if (!appChanged && permissionsLoaded) {
        console.log(`AppID '${appId}' already selected!`);
        return;
      }

      const features = [
        ...(app.Features ?? []),
        // 'disable-campaign',
      ];

      const account = getState().account;
      const isFlipdish = isFlipdishStaffEmail(account.Email);

      if (appChanged) {
        dispatch(clearPermissions());
      }

      void setFeatureFlagContext(account, app, isFlipdish);

      if (appChanged) {
        if (app.AppId) {
          setCurrentAppIdInStorage(app.AppId);
        }
        dispatch({
          type: appsConstants.SELECTED_APP_CHANGED,
          selectedApp: { ...app, Features: features },
        });
      }

      const showHiddenFeatures = getState().account.ShowHiddenFeatures;

      let basePermissions: string[];
      if (app.OrgId) {
        try {
          const response = await userPermissionsService.getOwnPermissions(app.OrgId);
          basePermissions = userPermissionsService.flattenPermissions(response);
        } catch (e) {
          console.warn(
            '[selectedAppChanged] auth-service permissions fetch failed; falling back to AppResourceSet.',
            e
          );
          basePermissions = (app.AppResourceSet ?? []) as unknown as string[];
        }
      } else {
        basePermissions = (app.AppResourceSet ?? []) as unknown as string[];
      }

      let permissions: AppState['permissions'] = [...basePermissions, app.AppAccessLevel!] as any;

      if (isFlipdish) {
        permissions.push('FlipdishStaff');
      }
      if (showHiddenFeatures) {
        permissions.push('ShowHiddenFeatures');
      }

      /** TEMPORARY quick unhide Customer Table Download CSV in prod (quicker for me coz don't have time to research split sht) */
      if (features.some((f) => f === 'portal-temporary-customer-table-header-unhide')) {
        permissions.push('TemporaryCustomerTableTitleShowHiddenFeature');
      }

      if (app.AppId === 'flipdish-global') {
        permissions = without(
          permissions,
          'CreateStoreGroups',
          'EditVouchers',
          'EditTeammates',
          'ViewTeammates',
          'ViewBankAccounts',
          'UpdateBankAccounts',
          'CreateBankAccounts',
          'SendPushNotificationToCustomer'
        );
      }

      const activeApp = getState().currentApp;
      const isStillSelected =
        appSelectionRequest === latestAppSelectionRequest &&
        activeApp.AppId === app.AppId &&
        (activeApp.OrgId ?? '') === (app.OrgId ?? '');
      if (!isStillSelected) {
        return;
      }

      dispatch(clearPermissions());
      dispatch(addRPermissions(permissions));
    } finally {
      if (isDirectSelection && appSelectionRequest === latestAppSelectionRequest) {
        dispatch(brandSwitchFinished());
      }
    }
  };
}

// #region getApps
const { GET_APPS, GET_APPS_SUCCESS, GET_APPS_FAILURE } = appsConstants;

export const getAppsRequest = (query?: string, page?: number, limit?: number) =>
  action(GET_APPS, { query, page, limit });

export const getAppsSuccess = (
  apps: App[],
  totalCount: number,
  query?: string,
  page?: number,
  limit?: number
) => action(GET_APPS_SUCCESS, { apps, totalCount }, { query, page, limit });

export const getAppsFailure = (error: Error, query?: string, page?: number, limit?: number) =>
  actionError(GET_APPS_FAILURE, { query, page, limit }, error);

export const getApps =
  (query?: string | undefined, page?: number, limit?: number): ThunkAction =>
  (dispatch, getState) => {
    dispatch(getAppsRequest(query, page, limit));
    return appsService
      .getApps(query, page, limit)
      .then((result) => {
        dispatch(getAppsSuccess(result.Data, result.TotalRecordCount, query, page, limit));
      })
      .catch((error) => {
        dispatch(getAppsFailure(error, query, page, limit));
      });
  };
// #endregion
function getAppsLegacy(
  currentAppId?: string | undefined,
  concat?: boolean,
  query?: string | undefined,
  page?: number,
  limit?: number
) {
  return (dispatch, getState) => {
    const timestamp = Date.now();

    dispatch(setTimestamp(timestamp));

    if (getState().apps.loading) {
      dispatch(addToAppsQueue(currentAppId, concat, query, page, limit));
      return null;
    }

    if (!concat) {
      dispatch(clearAndRequest());
    } else {
      dispatch(request());
    }
    return appsService.getApps(query, page, limit).then(
      (response) => {
        if (getState().apps.appsTimestamp === timestamp) {
          dispatch(setTimestamp(null));
        }

        if (concat) {
          dispatch(concatSuccess(response));
        } else {
          dispatch(success(response));
        }

        // The total number of results should be stored only with the empty query
        if (!query) {
          dispatch(totalAppsSuccess(response));
        }
      },
      (error) => {
        console.log(error);

        if (getState().apps.appsTimestamp === timestamp) {
          dispatch(setTimestamp(null));
        }

        dispatch(failure());
      }
    );
  };

  function addToAppsQueue(
    currentAppId?: string | undefined,
    concat?: boolean,
    query?: string | undefined,
    page?: number,
    limit?: number
  ) {
    const app = {
      currentAppId: undefined,
      concat: false,
      query: undefined,
      page: 1,
      limit: generalConstants.APP_LIMIT,
    };

    if (currentAppId) {
      (app as any).currentAppId = currentAppId;
    }

    if (concat) {
      app.concat = concat;
    }

    if (query) {
      (app as any).query = query;
    }

    if (page) {
      app.page = page;
    }

    if (limit) {
      app.limit = limit;
    }

    return {
      type: appsConstants.ADD_TO_APPS_QUEUE,
      payload: app,
    };
  }

  function setTimestamp(timestamp) {
    return {
      type: appsConstants.SET_APPS_TIMESTAMP,
      payload: timestamp,
    };
  }

  function clearAndRequest() {
    return {
      type: appsConstants.CLEAR_GET_APPS,
    };
  }

  function request() {
    return {
      type: appsConstants.GET_APPS,
    };
  }

  function failure() {
    return {
      type: appsConstants.GET_APPS_FAILURE,
    };
  }

  function success(response) {
    return {
      type: appsConstants.GET_APPS_SUCCESS,
      payload: response,
    };
  }

  function concatSuccess(response) {
    return {
      type: appsConstants.GET_APPS_SUCCESS_CONCAT,
      payload: response,
    };
  }

  function totalAppsSuccess(response) {
    return {
      type: appsConstants.GET_TOTAL_APPS_COUNT,
      payload: response.TotalRecordCount,
    };
  }
}

function processAppsQueue() {
  return (dispatch, getState) => {
    const appsQueue = getState().apps.appsQueue;

    if (appsQueue && appsQueue.length > 0) {
      const appQuery = appsQueue.shift();
      dispatch(
        getAppsLegacy(
          appQuery.currentAppId,
          appQuery.concat,
          appQuery.query,
          appQuery.page,
          appQuery.limit
        )
      );
      dispatch(setAppsQueue(appsQueue));
    }
  };

  function setAppsQueue(appsQueue) {
    return {
      type: appsConstants.SET_APPS_QUEUE,
      payload: appsQueue,
    };
  }
}

function getAppsNew(page: number, limit: number, query: string | undefined) {
  return (dispatch: ThunkDispatch) => {
    dispatch({ type: appsConstants.GET_APPS });
    const searchQuery = query === '' ? undefined : query;
    appsService.getApps(searchQuery, page, limit).then((response) => {
      dispatch({
        type: appsConstants.GET_APPS_SUCCESS_CONCAT,
        payload: response,
      });
    });
  };
}

const getAppsSucess = createAction<any>(appsConstants.GET_APPS_SUCCESS);

function getAppsWithQuery(query: string) {
  return (dispatch: ThunkDispatch) => {
    dispatch({ type: appsConstants.GET_APPS });
    dispatch(setSearchQuery(query));
    dispatch(appsActions.setPageNumber(1));
    const searchQuery = query === '' ? undefined : query;
    appsService.getApps(searchQuery, 1, 20).then((response) => {
      dispatch(getAppsSucess(response));
    });
  };
}

function setPageNumber(page: number) {
  return action(appsConstants.SET_PAGE_NUMBER, page);
}

function setSearchQuery(query: string) {
  return action(appsConstants.SET_SEARCH_QUERY, query);
}

const isOrgId = (appId: string) => {
  if (!appId.startsWith('org')) {
    return false;
  }
  const orgNumber = appId.slice(3);
  return !isNaN(Number(orgNumber)) && orgNumber.trim() !== '';
};

type SetCurrentAppOptions = {
  suppressNoAppFoundError?: boolean;
  /**
   * When true, active lookup failures rethrow instead of navigating home.
   * Lets AuthRouter distinguish network errors from empty app results.
   */
  throwOnError?: boolean;
};

/**
 * Looks up `appId` and makes it the current app. A switch away from a loaded app is flagged as
 * a brand switch for the page's loading state until the new app and its permissions land, or the
 * lookup fails. Only the latest request finishes the switch; superseded requests settle on it.
 */
function setCurrentApp(appId: string | undefined, options: SetCurrentAppOptions = {}) {
  return (dispatch: ThunkDispatch, getState: () => AppState): Promise<App | undefined> => {
    if (!appId || isOrgId(appId) || isPublicRoute(appId)) {
      return Promise.resolve(undefined);
    }

    const appSelectionRequest = ++latestAppSelectionRequest;
    // A brand switch has nothing to show until the app lookup and its permissions land, so flag it
    // for the page's loading state now rather than only once `clearPermissions` empties the routes.
    const isBrandSwitch =
      Boolean(getState().currentApp.AppId) && appId !== getState().currentApp.AppId;
    if (isBrandSwitch) {
      dispatch(brandSwitchStarted());
    }
    const selection = (async () => {
      try {
        const response = await appsService.getApps(appId);
        if (appSelectionRequest !== latestAppSelectionRequest) {
          const newerSelection = latestSetCurrentApp;
          if (newerSelection?.request === latestAppSelectionRequest) {
            return newerSelection.selection;
          }
          return getState().currentApp.AppId ? getState().currentApp : undefined;
        }
        if (response.Data.length) {
          const apps = response;
          dispatch(setSearchQuery(appId));
          dispatch(getAppsSucess(apps));
          const currentApp = apps.Data.find((a) => a.AppId === appId) || apps.Data[0];
          if (currentApp.AppId) {
            setCurrentAppIdInStorage(currentApp.AppId);
          }
          await dispatch(selectedAppChanged(currentApp, appSelectionRequest));
          if (appSelectionRequest !== latestAppSelectionRequest) {
            const newerSelection = latestSetCurrentApp;
            if (newerSelection?.request === latestAppSelectionRequest) {
              return newerSelection.selection;
            }
            return getState().currentApp.AppId ? getState().currentApp : undefined;
          }
          return currentApp;
        }
        if (!options.suppressNoAppFoundError) {
          const state = getState();
          const translate = getTranslate(state);
          dispatch(
            notifyError({
              message: translate('No_app_found_for_brandId', { appId }) as string,
            })
          );
        }
      } catch (error) {
        if (appSelectionRequest !== latestAppSelectionRequest) {
          const newerSelection = latestSetCurrentApp;
          if (newerSelection?.request === latestAppSelectionRequest) {
            return newerSelection.selection;
          }
          return getState().currentApp.AppId ? getState().currentApp : undefined;
        }
        if (options.throwOnError) {
          throw error;
        }
        history.replace('/');
      } finally {
        // A superseded request leaves the flag to the request that replaced it, whichever of the
        // two raised it.
        if (appSelectionRequest === latestAppSelectionRequest) {
          dispatch(brandSwitchFinished());
        }
      }
      return undefined;
    })();

    latestSetCurrentApp = { request: appSelectionRequest, selection };
    return selection;
  };
}

/** A brand switch is looking up its app; the page shows its loading state. */
function brandSwitchStarted() {
  return { type: appsConstants.BRAND_SWITCH_STARTED };
}

/** The brand switch has landed or failed; the page may render again. */
function brandSwitchFinished() {
  return { type: appsConstants.BRAND_SWITCH_FINISHED };
}

function toggleAppMenu(status: boolean) {
  return {
    type: appsConstants.TOGGLE_APP_MENU,
    payload: status,
  };
}

// #region panacea vanity url

export type SetPanaceaVanityUrl = ReturnType<typeof setPanaceaVanityUrl>;

function setPanaceaVanityUrl(appId: string, url: string) {
  return action(appsConstants.SET_APP_PANACEA_VANITY_URL_REQUEST, { url, appId });
}

function setPanaceaVanityUrlSuccess(url: string) {
  return action(appsConstants.SET_APP_PANACEA_VANITY_URL_SUCCESS, url);
}

function setPanaceaVanityUrlFailure(e) {
  return action(appsConstants.GET_APP_PANACEA_VANITY_URL_FAILURE, e);
}

function getPanaceaVanityUrlSuccess(url: string) {
  return action(appsConstants.GET_APP_PANACEA_VANITY_URL_SUCCESS, url);
}

function getPanaceaVanityUrlFailure(e) {
  return action(appsConstants.GET_APP_PANACEA_VANITY_URL_FAILURE, e);
}

// #end region
